import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, House, Notebook, ChartLine, User, UsersThree,
  Trash, Pencil, Check, X, Fire, CaretDown, CaretUp, Leaf, TrendDown, TrendUp, Minus
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from "recharts";

// Weight trend indicator component
const WeightTrend = ({ current, previous }) => {
  if (!current || !previous) return null;
  const diff = current - previous;
  const isLoss = diff < 0;
  const isGain = diff > 0;
  
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
      isLoss ? 'bg-emerald-100 text-emerald-700' : 
      isGain ? 'bg-rose-100 text-rose-700' : 
      'bg-gray-100 text-gray-600'
    }`}>
      {isLoss && <TrendDown size={14} weight="bold" />}
      {isGain && <TrendUp size={14} weight="bold" />}
      {!isLoss && !isGain && <Minus size={14} weight="bold" />}
      <span>{Math.abs(diff).toFixed(1)} kg</span>
    </div>
  );
};

const Diary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meals, setMeals] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
  const [loading, setLoading] = useState(true);
  const [editMeal, setEditMeal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [weightHistory, setWeightHistory] = useState([]);
  const [showWeightSection, setShowWeightSection] = useState(true);

  const fetchTodayMeals = async () => {
    try {
      const response = await axios.get(`${API}/meals/today`);
      setMeals(response.data.meals);
      setTotals(response.data.totals);
    } catch (error) {
      console.error('Meals error:', error);
      toast.error("Erro ao carregar refeições");
    } finally {
      setLoading(false);
    }
  };

  const fetchWeightHistory = async () => {
    try {
      const response = await axios.get(`${API}/weight/history`);
      const data = response.data.weights || response.data.history || [];
      // Format for chart - last 30 days
      const formatted = data.slice(0, 30).reverse().map(entry => ({
        date: new Date(entry.created_at).toLocaleDateString('pt', { day: '2-digit', month: 'short' }),
        weight: entry.weight,
        fullDate: entry.created_at
      }));
      setWeightHistory(formatted);
    } catch (error) {
      console.error('Weight history error:', error);
    }
  };

  useEffect(() => {
    fetchTodayMeals();
    fetchWeightHistory();
  }, []);

  const handleDelete = async (mealId) => {
    try {
      await axios.delete(`${API}/meals/${mealId}`);
      toast.success("Refeição removida");
      fetchTodayMeals();
    } catch (error) {
      toast.error("Erro ao remover refeição");
    }
  };

  const openEdit = (meal) => {
    setEditMeal(meal);
    setEditForm({
      name: meal.name,
      calories: meal.calories,
      protein: meal.protein,
      carbs: meal.carbs,
      fat: meal.fat
    });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/meals/${editMeal.id}`, editForm);
      toast.success("Refeição atualizada");
      setEditMeal(null);
      fetchTodayMeals();
    } catch (error) {
      toast.error("Erro ao atualizar refeição");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-primary font-bold text-xl">Nzadi Fit</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        <button 
          onClick={() => navigate('/')}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold">Diário Alimentar</h1>
      </header>

      <main className="p-4 space-y-4">
        {/* Daily Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary to-primary/80 text-white rounded-2xl p-5 shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-white/80 text-sm">Consumido Hoje</p>
              <p className="text-4xl font-bold font-mono">{totals.calories} <span className="text-lg font-normal">kcal</span></p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Fire size={32} className="text-white" weight="fill" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5">
              <p className="text-xl font-bold font-mono">{totals.protein}g</p>
              <p className="text-[10px] text-white/70 uppercase tracking-wider">Proteína</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5">
              <p className="text-xl font-bold font-mono">{totals.carbs}g</p>
              <p className="text-[10px] text-white/70 uppercase tracking-wider">Carbos</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5">
              <p className="text-xl font-bold font-mono">{totals.fat}g</p>
              <p className="text-[10px] text-white/70 uppercase tracking-wider">Gordura</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5">
              <p className="text-xl font-bold font-mono">{totals.fiber || 0}g</p>
              <p className="text-[10px] text-white/70 uppercase tracking-wider">Fibras</p>
            </div>
          </div>
        </motion.div>

        {/* Weight Progress Section */}
        {weightHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-card overflow-hidden"
          >
            <button
              onClick={() => setShowWeightSection(!showWeightSection)}
              className="w-full p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 rounded-full p-2">
                  <ChartLine size={20} className="text-blue-600" weight="bold" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-foreground">Evolução do Peso</h3>
                  <p className="text-xs text-muted-foreground">Últimos {weightHistory.length} registos</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {weightHistory.length >= 2 && (
                  <WeightTrend current={weightHistory[weightHistory.length - 1]?.weight} previous={weightHistory[0]?.weight} />
                )}
                <CaretDown size={18} className={`text-muted-foreground transition-transform ${showWeightSection ? 'rotate-180' : ''}`} />
              </div>
            </button>
            
            <AnimatePresence>
              {showWeightSection && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4">
                    {/* Current vs Target */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-blue-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold font-mono text-blue-600">
                          {weightHistory[weightHistory.length - 1]?.weight || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">Peso Atual (kg)</p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold font-mono text-emerald-600">
                          {user?.target_weight || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">Meta (kg)</p>
                      </div>
                    </div>
                    
                    {/* Chart */}
                    <div className="h-40 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weightHistory} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 10, fill: '#888' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis 
                            domain={['dataMin - 2', 'dataMax + 2']}
                            tick={{ fontSize: 10, fill: '#888' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #e5e7eb',
                              borderRadius: '12px',
                              fontSize: '12px'
                            }}
                            formatter={(value) => [`${value} kg`, 'Peso']}
                          />
                          {user?.target_weight && (
                            <ReferenceLine 
                              y={user.target_weight} 
                              stroke="#10b981" 
                              strokeDasharray="5 5"
                              label={{ value: 'Meta', position: 'right', fontSize: 10, fill: '#10b981' }}
                            />
                          )}
                          <Line 
                            type="monotone" 
                            dataKey="weight" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                            activeDot={{ r: 5, fill: '#3b82f6' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Quick Stats */}
                    {weightHistory.length >= 2 && (
                      <div className="mt-3 pt-3 border-t border-muted flex justify-between text-xs text-muted-foreground">
                        <span>Primeiro registo: {weightHistory[0]?.weight} kg</span>
                        <span>
                          Variação: {((weightHistory[weightHistory.length - 1]?.weight || 0) - (weightHistory[0]?.weight || 0)).toFixed(1)} kg
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Meals List */}
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-foreground text-lg">Refeições de Hoje</h2>
          <span className="text-sm text-muted-foreground">{meals.length} {meals.length === 1 ? 'refeição' : 'refeições'}</span>
        </div>
        
        {meals.length > 0 ? (
          <div className="space-y-3">
            <AnimatePresence>
              {meals.map((meal, index) => (
                <motion.div
                  key={meal.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-card overflow-hidden"
                >
                  {/* Main Card Content */}
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedMeal(expandedMeal === meal.id ? null : meal.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-foreground line-clamp-1">{meal.name}</h3>
                          {meal.is_angolan && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex-shrink-0">
                              🇦🇴
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {new Date(meal.created_at).toLocaleTimeString('pt', { hour: '2-digit', minute: '2-digit' })}
                          {meal.portion_modifier && meal.portion_modifier !== 1 && ` · ${meal.portion_modifier}x`}
                        </p>
                        {/* Compact Macros */}
                        <div className="flex gap-2 flex-wrap">
                          <span className="text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">{meal.protein}g prot</span>
                          <span className="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{meal.carbs}g carb</span>
                          <span className="text-xs bg-rose-100 text-rose-700 rounded-full px-2 py-0.5">{meal.fat}g gord</span>
                          {meal.fiber > 0 && (
                            <span className="text-xs bg-green-100 text-green-700 rounded-full px-2 py-0.5">{meal.fiber}g fibra</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-3">
                        <p className="text-2xl font-bold font-mono text-primary">{meal.calories}</p>
                        <p className="text-xs text-muted-foreground">kcal</p>
                        <button className="mt-1 p-1 text-muted-foreground hover:text-primary transition-colors">
                          {expandedMeal === meal.id ? <CaretUp size={18} /> : <CaretDown size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {expandedMeal === meal.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-muted"
                      >
                        <div className="p-4 bg-muted/30 space-y-3">
                          {/* Image if available */}
                          {meal.image_url && (
                            <img 
                              src={meal.image_url} 
                              alt={meal.name}
                              className="w-full h-32 object-cover rounded-xl"
                            />
                          )}
                          
                          {/* Detailed Macros Grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                              <p className="text-xl font-bold font-mono text-emerald-600">{meal.protein}g</p>
                              <p className="text-xs text-muted-foreground">Proteína</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                              <p className="text-xl font-bold font-mono text-amber-600">{meal.carbs}g</p>
                              <p className="text-xs text-muted-foreground">Carboidratos</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                              <p className="text-xl font-bold font-mono text-rose-500">{meal.fat}g</p>
                              <p className="text-xs text-muted-foreground">Gordura</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                              <p className="text-xl font-bold font-mono text-green-600">{meal.fiber || 0}g</p>
                              <p className="text-xs text-muted-foreground">Fibras</p>
                            </div>
                          </div>

                          {/* Health Feedback if available */}
                          {meal.health_feedback && (
                            <div className={`p-3 rounded-xl border-l-4 ${
                              meal.health_status === 'healthy' ? 'bg-emerald-50 border-emerald-500' :
                              meal.health_status === 'moderate' ? 'bg-amber-50 border-amber-500' :
                              'bg-rose-50 border-rose-500'
                            }`}>
                              <div className="flex items-center gap-2 mb-1">
                                <Leaf size={16} className={
                                  meal.health_status === 'healthy' ? 'text-emerald-600' :
                                  meal.health_status === 'moderate' ? 'text-amber-600' :
                                  'text-rose-600'
                                } />
                                <span className="text-xs font-semibold">
                                  {meal.health_status === 'healthy' ? 'Saudável' :
                                   meal.health_status === 'moderate' ? 'Moderada' : 'Atenção'}
                                </span>
                              </div>
                              <p className="text-sm text-foreground">{meal.health_feedback}</p>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openEdit(meal); }}
                              className="flex-1 rounded-full"
                              data-testid={`edit-meal-${meal.id}`}
                            >
                              <Pencil size={16} className="mr-1" /> Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDelete(meal.id); }}
                              className="flex-1 rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                              data-testid={`delete-meal-${meal.id}`}
                            >
                              <Trash size={16} className="mr-1" /> Remover
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <div className="text-4xl mb-4">🍽️</div>
            <p>Nenhuma refeição registrada hoje.</p>
            <p className="text-sm">Vai ao Dashboard e usa a câmera para começar!</p>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-muted/50 rounded-2xl p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">💡 Dica</p>
          <p>Toca no ícone do lápis para corrigir manualmente se a IA errou na identificação da comida.</p>
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={!!editMeal} onOpenChange={() => setEditMeal(null)}>
        <DialogContent className="bg-white rounded-3xl border-none max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle>Editar Refeição</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground">Nome</label>
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="input-nzadi mt-1"
                data-testid="edit-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground">Calorias</label>
                <input
                  type="number"
                  value={editForm.calories || ''}
                  onChange={(e) => setEditForm({ ...editForm, calories: parseInt(e.target.value) })}
                  className="input-nzadi mt-1"
                  data-testid="edit-calories-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Proteína (g)</label>
                <input
                  type="number"
                  step="0.1"
                  value={editForm.protein || ''}
                  onChange={(e) => setEditForm({ ...editForm, protein: parseFloat(e.target.value) })}
                  className="input-nzadi mt-1"
                  data-testid="edit-protein-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Carbos (g)</label>
                <input
                  type="number"
                  step="0.1"
                  value={editForm.carbs || ''}
                  onChange={(e) => setEditForm({ ...editForm, carbs: parseFloat(e.target.value) })}
                  className="input-nzadi mt-1"
                  data-testid="edit-carbs-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Gordura (g)</label>
                <input
                  type="number"
                  step="0.1"
                  value={editForm.fat || ''}
                  onChange={(e) => setEditForm({ ...editForm, fat: parseFloat(e.target.value) })}
                  className="input-nzadi mt-1"
                  data-testid="edit-fat-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMeal(null)} className="rounded-full">
              <X size={18} className="mr-1" /> Cancelar
            </Button>
            <Button onClick={handleSaveEdit} className="btn-primary">
              <Check size={18} className="mr-1" /> Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-muted p-2 flex justify-around items-center z-50 safe-bottom">
        {[
          { icon: House, label: "Início", path: "/" },
          { icon: Notebook, label: "Diário", path: "/diary", active: true },
          { icon: ChartLine, label: "Progresso", path: "/progress" },
          { icon: UsersThree, label: "Afiliados", path: "/affiliates" },
          { icon: User, label: "Perfil", path: "/profile" }
        ].map(({ icon: Icon, label, path, active }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
              active ? "text-primary" : "text-muted-foreground hover:text-primary"
            }`}
          >
            <Icon size={24} weight={active ? "fill" : "regular"} />
            <span className="text-xs mt-1">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default Diary;
